import { CheckboxIcon, ShieldExclamationIcon } from "@aureskonnect/react-ui";
import React from "react";
import { ModalFooter } from "reactstrap";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { toast } from "react-toastify";

import { store as userStore } from "@components/VerticalLayout/store";
import ModalAssociateStore from "./ModalAssociateStore";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";

import "@assets/swal.css";
import "@assets/theme.css";
import { publishProjectStatus } from "./hooks";
import { showWarningToast } from "@helpers/general";
import { useFetchGlobal } from "../../../../api/hooks";
import { showSuccessToast } from "@components/Common/SuccessToast";

type CardFooterModalPublishType = {
    rowData: any;
    setIsDataUpdated: any;
    setIsModesModalPublishOpened: Function;
};

export default function CardFooterModalPublish({
    rowData,
    setIsDataUpdated,
    setIsModesModalPublishOpened,
}: CardFooterModalPublishType) {
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const [isValidatedClicked, setIsValidatedClicked] = React.useState<boolean>(
        false
    );

    const {
        userID,
        franchiseID,
        oneShop,
        shopID,
        shopName,
        operatorID,
    } = useSnapshot(userStore);
    const uuidUser = localStorage.getItem("uuidUser");
    const { fetchGlobal } = useFetchGlobal();
    const { t } = useTranslation();
    // const { projectData } = useSnapshot(store);
    const [isDisabled, setIsDisabled] = React.useState(false);
    const [
        isValidateButtonDisabled,
        setIsValidateButtonDisabled,
    ] = React.useState<boolean>(false);

    function AssociateProject() {
        const idShops = rowData?.associates
            ? Object.keys(rowData.associates)
            : [];

        return [...new Set([...idShops, "0"])];
    }

    async function handleSaveButtonOnClickEvent() {
        let data: any = {};
        data[shopID] = {
            designation: shopName,
            id_boutique: shopID,
            hoursOfUse:
                rowData.hoursOfUse !== undefined ? rowData.hoursOfUse : "",
        };
        let dataAssociate: any[] = await AssociateProject();
        let savedData = {
            userId: userID,
            franchiseId: franchiseID,
            operatorId: operatorID,
            uuidUser: uuidUser,
            shopId: oneShop ? [shopID, "0"] : dataAssociate,
            projectId: rowData.projectId,
            status: "Publié",
            modified_at: `${moment().format("DD/MM/YYYY [at] h:mm a")}`,
            hour: `${moment().format("DD/MM/YY - HH:mm a")}`,
            designation: rowData[t("Designation")],
        };
        try {
            const result: any = await publishProjectStatus(
                savedData,
                fetchGlobal
            );

            const errors = result?.error ?? false;

            const toastConfig: any = {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                newestOnTop: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                icon: (
                    <ShieldExclamationIcon
                        width={25}
                        height={25}
                        fill="white"
                    />
                ),
            };
            if (!errors) {
                showSuccessToast(
                    `${t("The project is published successfully")!}`
                );
                setIsDataUpdated(true);
                setIsModesModalPublishOpened(false);
            }
            if (result.canPublish !== true) {
                showWarningToast(result, toastConfig);
            }
        } catch (e: any) {
            console.log(`${t("There's an error")}!`);
            setIsModesModalPublishOpened(false);
        }
    }
    async function handleSaveButtonOnClickEventDissociationShops() {
        // setIsModalLoadingOpened(true);
        // setModalLoadingData({
        //     title: "Publication in progress",
        //     message: "the publication of the template is in progress",
        // });
        if (isValidateButtonDisabled === false) {
            setIsValidateButtonDisabled(true);
            let dataAssociate: any[] = AssociateProject();
            let savedData = {
                userId: userID,
                franchiseId: franchiseID,
                shopId: dataAssociate,
                projectId: rowData.projectId,
                operatorId: operatorID,
                uuidUser: uuidUser,
                status: "Publié",
                modified_at: `${moment().format("DD/MM/YYYY [at] h:mm a")}`,
                hour: `${moment().format("DD/MM/YY - HH:mm a")}`,
                designation: rowData[t("Designation")],
            };
            try {
                const result: any = await publishProjectStatus(
                    savedData,
                    fetchGlobal
                );
                const hasError = result?.error ?? false;

                const toastConfig: any = {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000,
                    theme: "colored",
                    newestOnTop: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    icon: (
                        <ShieldExclamationIcon
                            width={25}
                            height={25}
                            fill="white"
                        />
                    ),
                };
                if (!hasError) {
                    toast.success(
                        `${t("The project is published successfully")!}`,
                        {
                            position: toast.POSITION.TOP_CENTER,
                            autoClose: 2000,
                            theme: "colored",
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            icon: (
                                <CheckboxIcon
                                    height={25}
                                    width={25}
                                    fill="white"
                                />
                            ),
                        }
                    );
                    setIsValidateButtonDisabled(false);
                    setIsDataUpdated(true);
                } else {
                    throw Error(hasError?.message);
                }
                if (result.canPublish !== true) {
                    showWarningToast(result, toastConfig);
                }
                setIsModalOpened(false);
            } catch (e: any) {
                setIsValidateButtonDisabled(false);
                console.log(`${t("There's an error")}!`, e);
            }
        }
    }

    React.useEffect(() => {
        if (isValidatedClicked) {
            handleSaveButtonOnClickEventDissociationShops();
        }
        // eslint-disable-next-line
    }, [isValidatedClicked]);

    return (
        <React.Fragment>
            <ModalFooter className="border-top-0">
                <CustomSecondaryColorButton
                    outline
                    variant="primary"
                    onClick={() => {
                        setIsModesModalPublishOpened(false);
                    }}
                    rounded
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    className="mr-2"
                    variant="primary"
                    disabled={isDisabled}
                    onClick={() => {
                        if (rowData?.publishAlert === true) {
                            setIsModalOpened(true);
                        } else {
                            setIsDisabled(true);
                            handleSaveButtonOnClickEvent();
                        }
                    }}
                >
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
            {isModalOpened ? (
                <ModalAssociateStore
                    setIsModalOpened={setIsModalOpened}
                    isModalOpened={isModalOpened}
                    setIsValidatedClicked={setIsValidatedClicked}
                    rowData={rowData}
                />
            ) : null}
        </React.Fragment>
    );
}
